// Regras de exibição de operações (item 23a/23b) — espelhos client-side.
// A AUTORIDADE é sempre a V2: o gate de cancelamento é o 409
// operation_cannot_be_cancelled dela (só WAITING_APPROVAL cancela —
// ~/zemo/v2 backend/app/api/v1/routes/operations.py). Aqui só se CONSOME o
// gate que o proxy do Portal repassa, sem duplicar regra de negócio.
import { DEFAULT_BACKING_TYPE } from "./backingTypes";
// Campos de dinheiro do wizard chegam MASCARADOS ("R$ 10.000,00"): `parseFloat`
// leria "10" e o payload iria com o valor ERRADO (money-path silencioso).
import { loadMoneyCentsBR, parseDecimalBR } from "./numberFormat";

// Único status cancelável na V2 (fonte acima). Usado apenas como fallback de
// exibição quando a linha vem do banco LOCAL (Dashboard "Recentes", fallback
// da lista) — a lista normalizada do proxy já traz `cancelavel` pronto.
export const V2_CANCELABLE_STATUS = "WAITING_APPROVAL";

/**
 * A ação "Cancelar" deve aparecer para esta operação?
 *
 * - Linha do proxy V2 normalizado: usa `cancelavel` (gate repassado da V2).
 *   Desde FS-094a esse gate é SÓ o status da V2 — op criada via API M2M (sem
 *   espelho local) também é cancelável, pelo id da V2 que a linha traz em `id`.
 * - Linha do banco local: precisa estar SINCRONIZADA com a V2
 *   (is_v2_synced) e no status cancelável de lá (v2_status). Órfã local
 *   (is_v2_synced=false, ex.: OP-998888) NUNCA oferece cancelar — a op não
 *   existe na V2 e a purga do registro é decisão separada do owner.
 *
 * Isto decide só a EXIBIÇÃO do botão; o enforcement real continua na V2
 * (o backend do Portal repassa o 409 dela, fail-closed).
 */
export function canCancelOperation(op) {
  if (!op) return false;
  if (op.cancelavel !== undefined && op.cancelavel !== null) return !!op.cancelavel;
  return !!op.is_v2_synced && op.v2_status === V2_CANCELABLE_STATUS;
}

/**
 * Linha vazia de recebível do wizard de Nova Operação (extraída do
 * NewOperation.jsx no item 23c para ser testável). `backing_type` nasce no
 * default histórico da V2 (NFE) — o Select do wizard parte dele.
 */
export function emptyReceivable() {
  return {
    _key: Date.now() + Math.random(),
    external_id: "",
    payer_name: "",
    payer_document: "",
    net_face_value: "",
    requested_advance_value: "",
    due_date: "",
    backing_type: DEFAULT_BACKING_TYPE,
    // FS-162: linha nasce no "default 100%" — enquanto ninguém decidir o valor
    // a antecipar, ele ACOMPANHA o valor de face (Q1). Ver `advanceValueFromPercent`.
    _advanceTouched: false,
  };
}

// ── FS-162: "Percentual antecipado" (owner 2026-08-21, Q1-Q8) ───────────────
//
// O % **não existe no estado**: o único número da linha continua sendo
// `requested_advance_value` (string mascarada), que é o que vai ao payload —
// `requested_..._percent` da V2 NÃO é usado, porque delegar o arredondamento
// contraria a decisão do owner de que o Portal manda o valor ABSOLUTO.
//
// A aritmética é ESPELHO de `resolve_percent_to_absolute`
// (~/zemo/v2 backend/app/domain/public_vocabulary.py): centavos inteiros,
// ROUND_DOWN no último centavo e **curto-circuito exato em 100%**. Por que
// espelhar em vez de aproximar: o NFV é o TETO do antecipável por definição
// (vault net-face-value), e a antecipação parcial gera um REMAINDER — um
// centavo de divergência entre o que a tela promete e o que a V2 calcula vira
// 422 ou resto bloqueado (vault face-liquid-value-e-antecipacao-parcial).
//
// A conta roda em `BigInt` sobre CENTAVOS e BASIS POINTS, nunca em float:
// `0.29 * 10000` em ponto flutuante é 2899,9999…, e um `floor` sobre isso
// devolveria R$ 28,99 para 29% de R$ 100,00 — erro de dinheiro invisível.

/** Faixa efetiva do campo (Q3): >0 a 100. O NFV é o teto do antecipável. */
export const ADVANCE_PERCENT_MAX = 100;
/** Casas do % exibido (Q2). */
export const ADVANCE_PERCENT_SCALE = 2;

/**
 * Hint PRÓPRIO do campo de percentual (Q5).
 *
 * Existe porque os dois campos vizinhos têm regimes de teclado OPOSTOS por
 * decisão do owner: no campo de reais "100" é R$ 1,00 (caixa registradora,
 * `MONEY_CENTS_HINT`) e aqui "100" é 100%. A divergência foi ratificada; o que
 * não pode é ficar implícita.
 *
 * O teto também é enunciado AQUI (UX-2): a rejeição acima de 100 é MUDA por
 * decisão (R1), e o hint do campo vizinho enuncia a regra estranha DELE — sem
 * esta frase, a única outra regra deste campo não existia em lugar nenhum
 * (nem rótulo, nem placeholder, nem `max`: o input é `type="text"`).
 */
export const ADVANCE_PERCENT_HINT =
  "Percentual do valor de face — digite normal (70,5 = 70,5%). Máximo 100.";

/**
 * Hint do campo % quando ele está DESABILITADO (UX-5).
 *
 * Sem valor de face legível o campo apaga e fica inerte — o certo, para não
 * exibir "NaN%" —, mas o hint continuava ensinando a digitar num campo em que
 * não se pode digitar. Aqui ele diz a ÚNICA ação que reabre o campo, e a mesma
 * frase vira o `title` do input (a razão passa a existir também no hover).
 */
export const advancePercentDisabledHint = (rotuloValorFace) =>
  `Preencha o ${rotuloValorFace} para usar o percentual.`;

/**
 * Aproximação DECLARADA (Q2): o % exibido tem 2 casas e é TRUNCADO, então nem
 * todo valor tem percentual exato. Quem manda no envio é o valor em reais.
 */
export const ADVANCE_PERCENT_APPROX_HINT =
  "Percentual aproximado — o valor em reais é o que vai no envio.";

/** Mensagem do teto por linha (Q4), com o rótulo do MODO (MODE_CFG). */
export const advanceExceedsFaceCopy = (rotuloValorFace) =>
  `Valor a antecipar maior que o ${rotuloValorFace} desta linha — reduza o valor ou o percentual.`;

/**
 * Mesma situação, dita no BANNER DO PASSO (UX-4).
 *
 * O banner é o ÚNICO elemento deste conjunto com `role="alert"` — é a frase que
 * um usuário de leitor de tela realmente ouve. Ela dizia "Há recebível
 * incompleto. Preencha todos os campos obrigatórios…" também no caso do teto,
 * em que NADA falta: os dois campos estão escritos e são números válidos. Era
 * literalmente falso, e mandava procurar um campo vazio que não existe — o
 * mesmo defeito que a UX-5 da FS-160 fechou na linha, reaberto um nível acima.
 *
 * Nota de manutenção: "reduza o valor ou o percentual" só é verdade porque a
 * edição DESCENDENTE do campo % é aceita mesmo acima de 100 (UX-1). Se alguém
 * voltar a rejeitar por valor absoluto, esta frase passa a apontar para um
 * controle inerte — as duas coisas andam juntas.
 */
export const advanceExceedsFaceBannerCopy = (rotuloValorFace) =>
  `Há recebível com valor a antecipar acima do ${rotuloValorFace}. ` +
  "Reduza o valor (ou o percentual) da linha sinalizada para continuar — " +
  "apenas recebíveis dentro do teto são enviados.";

/**
 * String de dinheiro do wizard → CENTAVOS inteiros, ou `null`.
 *
 * `parseDecimalBR` segue sendo a única fronteira de conversão (doutrina do
 * numberFormat): aqui só se quantiza o que ela devolveu. Vazio, texto cru
 * (NaN) e não-positivo saem `null` — é o que mantém o campo % NEUTRO em vez de
 * exibir "NaN%" ou fabricar R$ 0,00.
 */
export function moneyCents(v) {
  const n = parseDecimalBR(v);
  if (n === null || !Number.isFinite(n) || n <= 0) return null;
  const cents = Math.round(n * 100);
  // Fail-closed: `BigInt(Infinity)` LANÇA, e uma exceção num `onChange` derruba
  // a tela inteira. Inalcançável pela máscara (que tem teto de dígitos), mas
  // `moneyCents` é exportada e a próxima porta de carga pode não ter teto.
  //
  // RESIDUAL DECLARADO: acima de `Number.MAX_SAFE_INTEGER` centavos
  // (~R$ 90 trilhões) o valor já chega arredondado — é a precisão do
  // `parseDecimalBR`, a fronteira ÚNICA de conversão, e o payload tem
  // exatamente a mesma. Não se aperta aqui o que a tela aceita ali.
  return Number.isFinite(cents) ? cents : null;
}

/** CENTAVOS inteiros → "<int>,<2díg>" por STRING (sem round-trip por float). */
function centsToDecimalBR(cents) {
  const s = String(cents).padStart(3, "0");
  return `${s.slice(0, -2)},${s.slice(-2)}`;
}

/**
 * PERCENTUAL → valor a antecipar MASCARADO (ou `null` quando não há valor
 * derivável). Espelho de `resolve_percent_to_absolute`.
 *
 * A emissão passa por `loadMoneyCentsBR` — a porta PROGRAMÁTICA da máscara
 * (§Carga do numberFormat), nunca `maskMoneyCentsBR(v, prev)`: com `prev` o
 * texto entraria como DIGITAÇÃO e "7000,00" viraria R$ 700.000,00 (×100
 * silencioso). Sair pela porta certa também garante bytes idênticos aos que a
 * máscara escreve (NBSP incluído), então payload, total e rascunho não veem
 * diferença entre um valor digitado e um valor derivado do %.
 */
export function advanceValueFromPercent(netFaceValue, percent) {
  const baseCents = moneyCents(netFaceValue);
  if (baseCents === null) return null;
  if (!Number.isFinite(percent) || percent <= 0 || percent > ADVANCE_PERCENT_MAX) return null;
  // basis points INTEIROS: o campo tem 2 casas (Q2), então 70,5% = 7050 bp.
  const bp = Math.round(percent * 100);
  const cents =
    // Curto-circuito EXATO em 100%, LITERAL como na V2 — e EQUIVALENTE aqui,
    // declarado como tal em vez de presumido protetor. A V2 precisa dele
    // porque quantiza um `Decimal` que pode ter mais de 2 casas (100% de um NFV
    // com centavo ímpar sairia um centavo abaixo e um pedido válido viraria
    // 422 `STOCK_REQUESTED_VALUE_MISMATCH`); nesta ponta a base JÁ é um inteiro
    // de centavos, e `10000 × base ÷ 10000 = base` sem ele. Fica porque quem
    // comparar os dois códigos lado a lado tem de encontrar o mesmo ramo — o
    // espelho é a razão de o cálculo estar correto, e um espelho com um ramo a
    // menos exige a prova de novo. O comportamento está travado em teste
    // (100% de NFV com centavo ímpar), com ou sem esta linha.
    bp === ADVANCE_PERCENT_MAX * 100
      ? baseCents
      : Number((BigInt(bp) * BigInt(baseCents)) / 10000n); // ROUND_DOWN (trunca)
  if (cents <= 0) return null; // 0,01% de R$ 0,05 não fabrica R$ 0,00
  const texto = centsToDecimalBR(cents);
  const saida = loadMoneyCentsBR(texto);
  // A máscara REJEITA, nunca trunca (F7 da FS-160): acima do teto de dígitos
  // ela devolve o texto de ENTRADA intacto — é assim que se sabe que recusou,
  // sem duplicar aqui o teto que é dela. Deixar isso passar plantaria texto
  // NÃO MASCARADO num campo de dinheiro. Fail-closed: o percentual não
  // expressa esse valor, e o campo de reais segue aceitando o número direto.
  return saida === texto ? null : saida;
}

/**
 * VALOR → percentual DERIVADO (2 casas), com a exatidão declarada.
 *
 * `exact: false` quando o percentual de 2 casas não representa o valor: é o
 * gatilho do aviso de aproximação (Q2). TRUNCA (nunca arredonda para cima) —
 * mostrar 49,85% para R$ 1,66 de R$ 3,33 anunciaria um percentual que renderia
 * MAIS do que o valor que está no campo.
 *
 * Devolve o percentual honesto mesmo acima de 100 (valor > NFV): o clamp
 * esconderia o erro que a Q4 existe para mostrar.
 *
 * ⚠️ NUNCA re-derive o valor a partir daqui: a volta não é idempotente e
 * encolheria o dinheiro a cada ciclo. Só o texto que o usuário digitou no
 * campo % alimenta o sentido inverso.
 */
export function advancePercentFromValue(netFaceValue, advanceValue) {
  const baseCents = moneyCents(netFaceValue);
  const advCents = moneyCents(advanceValue);
  if (baseCents === null || advCents === null) return null;
  const escalado = BigInt(advCents) * 10000n;
  const base = BigInt(baseCents);
  return {
    percent: Number(escalado / base) / 100,
    exact: escalado % base === 0n,
  };
}

/** Percentual → texto pt-BR com 2 casas fixas ("70,00"). */
export function formatAdvancePercent(percent) {
  if (!Number.isFinite(percent)) return "";
  return percent.toFixed(ADVANCE_PERCENT_SCALE).replace(".", ",");
}

/**
 * Q4 — o valor a antecipar passou do valor de face DESTA linha?
 *
 * O NFV é o teto por DEFINIÇÃO (vault net-face-value; a V2 enforça por
 * desigualdade com 422 `requested_advance_value <= net_face_value`). Sem este
 * espelho, o usuário atravessava o wizard inteiro para descobrir no create.
 *
 * Só opina quando os DOIS lados são números válidos: linha crua/incompleta já
 * é reprovada pelo gate de campos, e dizer "excede" sobre um campo vazio
 * mandaria corrigir o que não existe.
 */
export function advanceExceedsFace(r) {
  const baseCents = moneyCents(r?.net_face_value);
  const advCents = moneyCents(r?.requested_advance_value);
  if (baseCents === null || advCents === null) return false;
  return advCents > baseCents;
}

// ── Item E: QUAIS campos faltam num recebível (não só "falta algo") ────────
// Antes, o wizard só sabia dizer `isValidReceivable(r) -> boolean` e mostrava
// um banner genérico: o usuário via "há recebível incompleto" e tinha de
// caçar o campo. Extraído para cá no molde do `signerMissingFields`
// (lib/representatives.js) — devolve a LISTA, que a tela usa para marcar o
// campo em vermelho.
//
// `test` reproduz a semântica EXATA do predicado que vivia no componente
// (`NewOperation.jsx:545-549`): truthiness crua para texto/data e
// `> 0` para dinheiro. Nada de `.trim()` — endurecer o gate seria mudança de
// regra, não de UX. O `parseFloat` virou `parseDecimalBR` com a máscara de
// input (2026-08-02): MESMA semântica de gate — vazio→null e inválido→NaN
// reprovam em `> 0` exatamente como o NaN do `parseFloat` reprovava.
export const RECEIVABLE_REQUIRED_FIELDS = [
  ["external_id", "ID externo (NF)", (v) => !!v],
  ["payer_name", "sacado", (v) => !!v],
  ["payer_document", "documento do sacado", (v) => !!v],
  ["net_face_value", "valor", (v) => parseDecimalBR(v) > 0],
  ["requested_advance_value", "valor a antecipar", (v) => parseDecimalBR(v) > 0],
  ["due_date", "data de vencimento", (v) => !!v],
];

// Chaves dos campos que faltam para o recebível poder ser enviado.
export function receivableMissingFields(r) {
  return RECEIVABLE_REQUIRED_FIELDS
    .filter(([key, , test]) => !test(r?.[key]))
    .map(([key]) => key);
}

/**
 * Dos campos reprovados, os que estão PREENCHIDOS mas INVÁLIDOS (UX-5 do
 * /myuxreview, FS-160). Sem esta separação a linha dizia "Falta preencher:
 * valor" com o valor visivelmente escrito no campo: a máscara deixa texto cru
 * VISÍVEL de propósito (doutrina 2 do numberFormat), então "falta preencher"
 * era literalmente falso e o usuário não tinha como saber o que corrigir.
 *
 * Na prática só campo de dinheiro cai aqui — o teste dos demais é truthiness,
 * então preenchido ⇒ aprovado. A regra é escrita de forma geral mesmo assim.
 */
export function receivableInvalidFields(r) {
  return RECEIVABLE_REQUIRED_FIELDS
    .filter(([key, , test]) => !test(r?.[key]) && !!String(r?.[key] ?? "").trim())
    .map(([key]) => key);
}

/** Reprovados de fato VAZIOS (complemento de `receivableInvalidFields`). */
export function receivableEmptyFields(r) {
  const invalidos = receivableInvalidFields(r);
  return receivableMissingFields(r).filter((key) => !invalidos.includes(key));
}

// Rótulos PT-BR por chave (a tela monta a frase; o modo cedente sobrescreve o
// rótulo do sacado, que lá é um Select único de cliente).
export const RECEIVABLE_FIELD_LABELS = Object.fromEntries(
  RECEIVABLE_REQUIRED_FIELDS.map(([key, label]) => [key, label])
);

/**
 * Linha pronta para ir à V2.
 *
 * FS-162/Q4: o teto `requested_advance_value <= net_face_value` entra AQUI, e
 * não em `RECEIVABLE_REQUIRED_FIELDS`, porque é regra CRUZADA entre dois campos
 * preenchidos — não é campo faltando. Entrar por aqui é o que basta: a linha
 * sai de `validReceivables` (logo, do payload e do total), cai em
 * `hasPartialReceivable` e o passo trava. A frase que o usuário lê é PRÓPRIA
 * (`advanceExceedsFaceCopy`): "Falta preencher" com os dois campos escritos na
 * tela seria literalmente falso, o mesmo defeito que a UX-5 da FS-160 fechou.
 */
export const isReceivableComplete = (r) =>
  receivableMissingFields(r).length === 0 && !advanceExceedsFace(r);

// Linha "mexida": tem QUALQUER campo obrigatório preenchido. Uma linha pristine
// não é erro — marcá-la de vermelho faria o leitor de tela anunciar falha num
// recebível recém-adicionado (a11y). Espelha o antigo `isFilledReceivable`.
export const isReceivableTouched = (r) =>
  RECEIVABLE_REQUIRED_FIELDS.some(([key]) => !!r?.[key]);

/**
 * Recebível do estado do wizard → payload do POST /operations (contrato
 * DirectOperationCreate da V2). Molde do signer_ids (item 22): campo opcional
 * AUSENTE não é enviado — recebível sem backing_type (estado antigo/limpo)
 * deixa a V2 aplicar o default dela (NFE), em vez de o front inventar valor.
 */
export function mapReceivablePayload(r) {
  const payload = {
    external_id: r.external_id,
    payer_name: r.payer_name,
    payer_document: r.payer_document,
    // `?? NaN`: o shape do payload não muda com a máscara — campo vazio
    // continua produzindo NaN (era `parseFloat("")`), não null.
    net_face_value: parseDecimalBR(r.net_face_value) ?? NaN,
    requested_advance_value: parseDecimalBR(r.requested_advance_value) ?? NaN,
    due_date: r.due_date,
  };
  if (r.backing_type) payload.backing_type = r.backing_type;
  return payload;
}

// ── Item 16: bloco consultivo `policy_verdict` da simulação V2 ──────────────
// A V2 devolve, junto dos números (resposta segue 200), o veredito que o
// CREATE daria: {would_reject, reason, allowed_range:{min_pct,max_pct}|null}.
// reason é a MESMA message do 422 CET_OUT_OF_BOUNDS do create. O wizard usa
// isto para avisar/bloquear JÁ na etapa de recebíveis — antes, taxa fora da
// faixa só estourava na etapa final. Consultivo: o enforcement real segue no
// create (422, defesa em profundidade).

/**
 * Extrai o bloco `policy_verdict` da resposta de simulação, com fail-safe:
 * ausente (V2 antiga) ⇒ null SILENCIOSO — o wizard trata como "sem veredito"
 * e mantém o comportamento atual. Bloco PRESENTE mas fora do contrato
 * (não-objeto, ou `would_reject` não-bool — bloco parcial) ⇒ também null,
 * mas com console.warn: sem o sinal, uma quebra de contrato da V2 em prod
 * degradaria a rede de segurança para "sempre libera" em silêncio. Só
 * observabilidade — nunca bloqueia por lixo (consultivo; o create segue
 * como autoridade).
 */
export function getPolicyVerdict(simulationResult) {
  if (!simulationResult || typeof simulationResult !== "object") return null;
  if (!("policy_verdict" in simulationResult)) return null; // V2 antiga: silêncio
  const v = simulationResult.policy_verdict;
  const wellFormed =
    !!v && typeof v === "object" && !Array.isArray(v) && typeof v.would_reject === "boolean";
  if (!wellFormed) {
    // Padrão de observabilidade do wizard (console.error no load de
    // produtos/cedentes): logar e seguir sem quebrar a experiência.
    console.warn(
      "[NewOperation] policy_verdict presente mas fora do contrato — ignorado (sem veredito):",
      v
    );
    return null;
  }
  return v;
}

/**
 * A simulação corrente seria REJEITADA pelo create? Estrito (`=== true`,
 * contrato da V2 é bool): valor fora do contrato não bloqueia o usuário —
 * é consultivo, e o create continua sendo a autoridade.
 */
export function isSimulationRejected(simulationResult) {
  return getPolicyVerdict(simulationResult)?.would_reject === true;
}

/**
 * "Faixa permitida: 1.5% a 4.5% a.m." — null sem banda completa (a V2 manda
 * allowed_range=null quando o produto não tem banda; % a.m. como strings
 * decimais, exibidas como vêm, sem reformatar dinheiro/taxa no front).
 */
export function formatAllowedRange(allowedRange) {
  if (!allowedRange || allowedRange.min_pct == null || allowedRange.max_pct == null) return null;
  return `Faixa permitida: ${allowedRange.min_pct}% a ${allowedRange.max_pct}% a.m.`;
}

// Fallback quando would_reject=true vier sem reason (contrato prevê reason
// preenchido na rejeição; defensivo). Mesmo vocabulário do formatV2Detail.
export const POLICY_REJECT_FALLBACK =
  "A operacao seria recusada pela politica de taxas deste produto. " +
  "Ajuste a taxa mensal ou os valores dos recebiveis.";

/** Motivo exibido na rejeição: `reason` da V2 INTACTO (é a mesma message do
 * 422 do create); fallback genérico se vier vazio. */
export function policyRejectionReason(verdict) {
  const r = verdict?.reason;
  return typeof r === "string" && r.trim() ? r : POLICY_REJECT_FALLBACK;
}

/**
 * Formata taxa/prazo tolerando ausência (op V2 sem espelho local não tem
 * taxa_mensal/prazo_dias — antes renderizava "null% · undefinedd").
 */
export function formatTaxaPrazo(op) {
  if (op?.taxa_mensal === null || op?.taxa_mensal === undefined) return "—";
  const prazo = op.prazo_dias === null || op.prazo_dias === undefined ? "—" : `${op.prazo_dias}d`;
  return `${op.taxa_mensal}% · ${prazo}`;
}

// ── Item do ESTOQUE (POST /v2/stock) ────────────────────────────────────────
// Mesma doutrina do RECEIVABLE_REQUIRED_FIELDS, e pelo mesmo motivo: o gate do
// `handleRegister` era truthy (`!item.gross_face_value`), então texto CRU
// inválido ("1e5", "9.2,00") passava, virava NaN→null no POST e o usuário via
// toast de SUCESSO. Pior: o lote é sequencial, então os itens anteriores já
// estavam persistidos quando o inválido chegava ao backend.
export const STOCK_ITEM_REQUIRED_FIELDS = [
  ["assignor_id", "cedente", (v) => !!v],
  ["payer_id", "sacado", (v) => !!v],
  ["external_id", "ID externo (NF)", (v) => !!v],
  ["gross_face_value", "valor bruto", (v) => parseDecimalBR(v) > 0],
  ["net_face_value", "valor líquido", (v) => parseDecimalBR(v) > 0],
  ["due_date", "vencimento", (v) => !!v],
];

/** Linha com ALGUM campo obrigatório preenchido (linha em branco não conta). */
export function isFilledStockItem(item) {
  return STOCK_ITEM_REQUIRED_FIELDS.some(([campo]) => !!String(item?.[campo] ?? "").trim());
}

/**
 * Primeira linha PARCIAL/inválida do lote — a validar ANTES de qualquer POST.
 * Linha totalmente em branco é ignorada (o form começa com uma); linha com
 * algo digitado tem de estar completa e válida, senão nada é enviado.
 *
 * @returns {string|null} mensagem para o toast, ou null quando pode seguir
 */
export function stockItemsError(items) {
  const lista = Array.isArray(items) ? items : [];
  for (let i = 0; i < lista.length; i += 1) {
    if (!isFilledStockItem(lista[i])) continue;
    const faltando = STOCK_ITEM_REQUIRED_FIELDS
      .filter(([campo, , test]) => !test(lista[i][campo]))
      .map(([, rotulo]) => rotulo);
    if (faltando.length) {
      return `Recebível ${i + 1}: corrija ${faltando.join(", ")} — nada foi registrado.`;
    }
  }
  return lista.some(isFilledStockItem)
    ? null
    : "Preencha ao menos um recebível para registrar no estoque.";
}

/**
 * TOTAL do lote do estoque, para conferência ANTES do POST (UX-6 do
 * /myuxreview, FS-160). O lote é sequencial e sem tela de revisão: a primeira
 * confirmação do valor fora do próprio campo acontecia DEPOIS do POST — e com
 * a caixa registradora um dígito a mais ou a menos é um ×10 que o usuário
 * precisa ter onde perceber.
 *
 * Soma pelo `parseDecimalBR` — a MESMA fronteira do payload, nunca `parseFloat`
 * sobre a string mascarada.
 *
 * B2 (parecer do round 3): a unidade do total é o ITEM, não a coluna. Um item
 * com bruto válido e líquido inválido fica fora das DUAS somas, não só da
 * coluna quebrada. Somando por coluna, a tela dizia "fora do total" para um
 * item cujo bruto ESTAVA no total — copy mentindo —, e o "Total bruto" mudava
 * sozinho quando o usuário corrigia um campo da OUTRA coluna. Por item também
 * é o que casa com o resto: o item é a unidade do POST, e um item inválido
 * bloqueia o lote inteiro no `stockItemsError` — ele não vai a lugar nenhum.
 *
 * `count` = itens PREENCHIDOS; `invalidos` = quantos deles ficaram fora das
 * somas. Logo `count - invalidos` é exatamente o que o total representa.
 *
 * @returns {{count: number, gross: number, net: number, invalidos: number}}
 */
export function stockItemsTotals(items) {
  const lista = (Array.isArray(items) ? items : []).filter(isFilledStockItem);
  const completo = (item) =>
    parseDecimalBR(item?.gross_face_value) > 0 && parseDecimalBR(item?.net_face_value) > 0;
  const validos = lista.filter(completo);
  const somar = (campo) => validos.reduce((soma, item) => soma + parseDecimalBR(item[campo]), 0);
  return {
    count: lista.length,
    gross: somar("gross_face_value"),
    net: somar("net_face_value"),
    invalidos: lista.length - validos.length,
  };
}

/**
 * Override de TAXA preenchido mas inválido ⇒ mensagem de bloqueio.
 *
 * Campo vazio significa "use a policy da V2"; campo com texto que não parseia
 * produzia `taxaNum = NaN`, o ramo `taxaNum > 0` era falso e a simulação/
 * criação seguia pela policy em SILÊNCIO — o usuário achava que tinha travado
 * a taxa (e o create ainda mandava `taxa_mensal: NaN`, que vira null no JSON).
 *
 * Função PURA e compartilhada (o `stockFeesError` reusa esta mesma regra e
 * mensagem) para que a tela não carregue lógica de validação de money-path.
 *
 * @param {string|number|null|undefined} taxa valor do campo, mascarado
 * @returns {string|null} mensagem para o toast, ou null quando pode seguir
 */
export function taxaOverrideError(taxa) {
  if (String(taxa ?? "").trim() === "") return null; // vazio = policy da V2
  if (Number.isFinite(parseDecimalBR(taxa))) return null;
  return "Taxa mensal (%) inválida — corrija o valor (ex.: 3,5) ou deixe o campo em branco para usar a policy da V2.";
}
